import { Box, CircularProgress, Dialog, Paper } from "@material-ui/core";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { OutletUserExternalCredentialsRoute } from "../../constants/routePaths";
import OutletUserExternalCredentials from "../../routing/OutletUserExternalCredentials";
import {
  createUpdateOutletUserExternalCredentials,
  getOutletUserExternalCredentialsById,
  revealOutletUserExternalCredentials,
} from "../../services/OutletUserExternalCredentials/outletUserExternalCredentialsApis";
import { externalApplications } from "../../constants/externalApplications";
import { DisplayMessage, GetSortedOptionsByName } from "../../services/helpers";
import DelayedRedirect from "../../utils/DelayedRedirect";
import { Transition } from "../../utils/ModalHelpers";
import AppBarComponent from "../Common/Headers/AppBarComponent";
import EnhancedDropdownComponent from "../Common/Dropdowns/EnhancedDropdownComponent";
import PasswordInputComponent from "../Common/Inputs/PasswordInputComponent";
import { decryptAes256Gcm, encryptAes256Gcm } from "../../utils/aes256";
import ModifiedByInfo from "../Common/Footers/ModifiedByInfo";
import TextFieldComponent from "../Common/Inputs/TextFieldComponent";
import OutletsDropdown from "../Common/Dropdowns/OutletsDropdown";

const ExternalCredentialsItem = ({
  resources,
  outlets,
  outletUserExternalCredentials,
  pathId,
}) => {
  const isEditMode = pathId !== undefined;
  const [redirect, setRedirect] = useState(null);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [formData, setFormData] = useState({
    application: 0,
    username: "",
    password: "",
    outletId: 0,
    id: outletUserExternalCredentials?.id,
  });
  const [modifiedBy, setModifiedBy] = useState({
    avatar: outletUserExternalCredentials?.createdByAvatar,
    on: outletUserExternalCredentials?.createdOn,
    name: outletUserExternalCredentials?.createdByName,
  });

  const isValidForm = () => {
    const { username, password, application, outletId } = formData;

    if (!username || username.trim() === '') {
      return false;
    }

    if (!password || password.trim() === '') {
      return false;
    }

    if (!application || application.trim() === '') {
      return false;
    }

    if (!outletId) {
      return false;
    }

    return true;
  }


  const autoFillForm = useCallback(async () => {
    setRequestInProgress(true);

    let credentials = outletUserExternalCredentials;
    if (credentials === null || credentials === undefined) {
      const credentialsResult = await getOutletUserExternalCredentialsById(
        pathId
      );
      if (!credentialsResult) {
        setRedirect(OutletUserExternalCredentials);
        setRequestInProgress(false);
      }

      credentials = credentialsResult[0];
    }

    const passwordResult = await revealOutletUserExternalCredentials(
      parseInt(credentials?.id)
    );
    if (!passwordResult) {
      setRedirect(OutletUserExternalCredentials);
    }

    const decryptedPassword = await decryptAes256Gcm(passwordResult.password);
    setFormData({
      outletId: credentials?.outletId,
      application: credentials?.application,
      username: credentials?.username,
      id: credentials?.id,
      password: decryptedPassword,
    });
    setModifiedBy({
      avatar: credentials?.createdByAvatar,
      on: credentials?.createdOn,
      name: credentials?.createdByName,
    });

    setRequestInProgress(false);
  }, [outletUserExternalCredentials, pathId]);

  useEffect(() => {
    if (isEditMode) {
      autoFillForm();
    }
    if (!outlets) {
      setRedirect(OutletUserExternalCredentialsRoute);
    }
  }, [isEditMode, autoFillForm, outlets]);

  const handleClose = () => {
    setDialogOpen(false);
    setRedirect(OutletUserExternalCredentialsRoute);
  };

  const handleSubmit = async () => {
    if (!isValidForm()) {
      return;
    }

    setRequestInProgress(true);
    const { username, password, outletId, application, id } =
      formData;

    const encryptedPassword = await encryptAes256Gcm(password);
    const body = JSON.stringify({
      username,
      password: encryptedPassword,
      outletId,
      application,
      id,
    });

    const result = await createUpdateOutletUserExternalCredentials(body);

    if (result) {
      setRefreshGrid(true);
      const successMessage = isEditMode
        ? resources["ExternalCredentialsUpdatedWithSuccess"]
        : resources["ExternalCredentialsCreatedWithSuccess"];
      DisplayMessage(successMessage, "success");
    }
    setRedirect(OutletUserExternalCredentialsRoute);
  };

  const applicationOptions = useMemo(
    () => [
      {
        name: resources["DBB"],
        key: externalApplications.DBB,
      },
    ],
    [resources]
  );

  const handleFormChange = (key, value) => {
    setFormData((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const isSaveButtonDisabled = requestInProgress || !isValidForm() || redirect

  return (
    <Box>
      {redirect && (
        <DelayedRedirect
          to={{ pathname: redirect, state: { refreshGrid: refreshGrid } }}
          delay={250}
        />
      )}
      <Dialog
        fullScreen
        open={dialogOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBarComponent
          idName="externalCredentials"
          title={
            isEditMode
              ? resources["EditExternalCredentials"]
              : resources["NewExternalCredentials"]
          }
          resources={resources}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          disabled={isSaveButtonDisabled}
        />
        {requestInProgress ? (
          <Box className="centered-loader fadeIn">
            <CircularProgress />
          </Box>
        ) : (
          <Box className="container fadeIn">
            <Box className="py-4 grey">
              <Paper elevation={3} className="p-4 mt-3 mb-3">
                <Box className="d-flex form-group pt-3">
                  <Box className="col-3" />
                  <Box className="col-6">
                    <TextFieldComponent
                      value={formData.username}
                      handleChange={(e) =>
                        handleFormChange("username", e.target.value.replace(/^\s+/, ""))
                      }
                      labelText={resources["UserName"]}
                      id="username"
                      maxLength={50}
                      placeholderText={resources["InsertUsername"]}
                      helperText={resources["InputOfUsernameIsMandatory"]}
                      required
                    />
                    <PasswordInputComponent
                      value={formData.password}
                      handleChange={(e) =>
                        handleFormChange("password", e.target.value)
                      }
                      placeholder={resources["InsertPassword"]}
                      labelText={resources["Password"]}
                      id="password"
                      maxLength={64}
                      helperText={resources["InputOfPasswordIsMandatory"]}
                      isPassword={true}
                      required
                    />
                    <EnhancedDropdownComponent
                      value={formData.application}
                      handleChange={(e) =>
                        handleFormChange("application", e.target.value)
                      }
                      options={GetSortedOptionsByName(applicationOptions)}
                      labelText={resources["Application"]}
                      id="application"
                      disabled={isEditMode}
                      placeholder={resources["SelectApplication"]}
                      helperText={isEditMode ? "" : resources["InputOfApplicationIsMandatory"]}
                      error={!formData.application}
                      required
                    />
                    <OutletsDropdown
                      resources={resources}
                      outlets={outlets}
                      outlet={formData.outletId}
                      onOutletChange={(e) =>
                        handleFormChange("outletId", parseInt(e.target.value))
                      }
                      required={outlets.length > 0}
                      disabled={isEditMode}
                      helperText={resources["SelectionOfOutletIsMandatory"]}
                      defaultValue={resources["SelectOutlet"]}
                    />
                  </Box>
                  <Box className="col-3" />
                </Box>
                {isEditMode && (
                  <ModifiedByInfo 
                    className="mt-3"
                    resources={resources}
                    modifiedByAvatar={modifiedBy.avatar}
                    modifiedOn={modifiedBy.on}
                    modifiedByName={modifiedBy.name}   
                    isCreated={true}    
                  />
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default ExternalCredentialsItem;
